import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { ClubsPage } from "./clubs";
import { searchClubs } from "../lib/api/lidoo.functions";
import { DICTS, LOCALIZED_LANGS, type Lang } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

// Kept in step with the English route on purpose: a filter that works on
// /clubs but drops the visitor's selection on /nl/clubs is worse than no
// filter at all.
const searchSchema = z.object({
  q: z.string().optional(),
  region: z.string().optional(),
  near: z.string().optional(),
  f: z.string().optional(),
  priced: z.boolean().optional(),
  page: z.number().int().min(1).max(200).optional(),
});

// "lat,lng" from the URL, as the home page's "near me" button writes it. Sent
// to the server so the whole catalogue is ordered by distance before paging,
// not just the page that happened to come back.
function parseNear(raw: string | undefined): { lat: number; lng: number } | undefined {
  if (!raw) return undefined;
  const [lat, lng] = raw.split(",").map(Number);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return undefined;
  return { lat, lng };
}

export const Route = createFileRoute("/$locale/clubs")({
  beforeLoad: ({ params }) => {
    if (params.locale === "en") throw redirect({ to: "/clubs" });
    if (!LOCALIZED_LANGS.includes(params.locale as Lang)) throw notFound();
  },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) =>
    await searchClubs({
      data: {
        q: deps.q,
        region: deps.region,
        facilities: deps.f ? deps.f.split(",").filter(Boolean) : undefined,
        priced: deps.priced,
        page: deps.page,
        near: parseNear(deps.near),
      },
    }),
  head: (ctx) => {
    const lang = ctx.params.locale as Lang;
    const d = DICTS[lang];
    return {
      meta: [
        { title: `${d.nav_explore} | sunbedly` },
        { name: "description", content: d.meta_clubs_desc },
      ],
      links: hreflangLinks("/clubs", ctx.params.locale),
    };
  },
  component: ClubsPage,
});
